import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Phone, Calendar, Clock, Sparkles } from "lucide-react";
import { format } from "date-fns";

interface ScheduledCall {
  id: string;
  scheduled_for: string;
  status: string;
  child_id: string;
  pattern: string;
}

interface ScheduledCallsBannerProps {
  caregiverId: string | null;
}

export function ScheduledCallsBanner({ caregiverId }: ScheduledCallsBannerProps) {
  const { data: scheduledCalls = [], isLoading } = useQuery({
    queryKey: ['caregiver-scheduled-calls', caregiverId],
    queryFn: async () => {
      if (!caregiverId) return [];
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('scheduled_bot_calls')
        .select('id, scheduled_for, status, child_id, pattern')
        .eq('caregiver_id', caregiverId)
        .in('status', ['scheduled', 'retry'])
        .gte('scheduled_for', new Date().toISOString())
        .order('scheduled_for', { ascending: true })
        .limit(5);

      if (error) {
        console.error('[ScheduledCallsBanner] Error fetching calls:', error);
        return [];
      }
      
      return (data || []) as ScheduledCall[];
    },
    enabled: !!caregiverId,
    refetchInterval: 60000, // Refresh every minute
  });

  if (isLoading || scheduledCalls.length === 0) {
    return null;
  }

  return (
    <Card className="border-purple-500/30 bg-purple-500/5 dark:bg-purple-500/10 animate-fade-in">
      <CardHeader className="pb-2 sm:pb-3 p-3 sm:p-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="relative">
            <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500" />
            <Sparkles className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 text-purple-400" />
          </div>
          <div>
            <CardTitle className="text-base sm:text-lg text-purple-600 dark:text-purple-400 flex items-center gap-2">
              Upcoming AI Check-ins
            </CardTitle>
            <CardDescription className="text-2xs sm:text-sm">
              Your scheduled calls with the AI Sleep Coach
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 px-3 sm:px-6 pb-3 sm:pb-6">
        <div className="space-y-2">
          {scheduledCalls.map((call) => (
            <div 
              key={call.id} 
              className="flex items-center justify-between gap-2 p-2 sm:p-3 rounded-lg bg-background/50 border border-purple-500/20"
            >
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="hidden sm:flex items-center justify-center w-8 h-8 rounded-full bg-purple-500/10">
                  <Calendar className="w-4 h-4 text-purple-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm font-medium truncate">
                    {format(new Date(call.scheduled_for), 'MMM d, yyyy')}
                  </p>
                  <div className="flex items-center gap-1 text-2xs sm:text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    {format(new Date(call.scheduled_for), 'h:mm a')}
                  </div>
                </div>
              </div>
              <Badge 
                variant="secondary" 
                className="bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/30 text-2xs sm:text-xs capitalize shrink-0"
              >
                {call.status === 'retry' ? 'Rescheduled' : 'Scheduled'}
              </Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
